import { useState } from 'react';

import { Permissions } from '@cloud-wave/neon-common-lib';
import { TRingtoneNames } from '@cloud-wave/neon-common-lib/app';

import { useConfigContext } from 'lib/core/config';

import { useLocalStorage } from 'lib/common/hooks/useLocalStorage';

import INCOMING_TASK_PLACEMENTS from 'lib/common/constants/incomingTaskPlacements';
import WORKSPACE_LAYOUTS from 'lib/common/constants/workspaceLayouts';

import { isWindows } from 'lib/common/utils/isWindows';
import { getDefaultRingtoneName } from 'lib/common/utils/ringtone/getDefaultRingtoneName';

import Context, { LogStreamFilter } from './Context';

const DEFAULT_INCOMING_TASK_PLACEMENT = isWindows() ? INCOMING_TASK_PLACEMENTS.TOP : INCOMING_TASK_PLACEMENTS.BOTTOM;
const DEFAULT_ENHANCED_LOGGING_STATE = false;
const DEFAULT_INCOMING_CHAT_NOTIFICATIONS_STATE = true;

const PREFERENCE_IDS = {
  DEFAULT_WORKSPACE_LAYOUT: 'default-workspace-layout',
  RINGTONE: 'ringtone',
  INCOMING_TASK_PLACEMENT: 'incoming-task-placement',
  ENHANCED_LOGGING_ENABLED: 'enhanced-logging-enabled',
  INCOMING_CHAT_NOTIFICATIONS_ENABLED: 'incoming-chat-notifications-enabled',
};

/** This context is only for global preferences that are needed by multiple components.
 * Other agent preferences can be found in the preferences overlay **/
function PreferencesProvider({ children }) {
  const { getStorageItem, setStorageItem } = useLocalStorage();

  const { config } = useConfigContext();

  const { PERMISSIONS = [], DEFAULT_RINGTONE } = config;
  const hasRestrictedRingtonePermission = PERMISSIONS.includes(Permissions.RESTRICTED_RINGTONE);

  function getInitialPreferenceValue(id, defaultValue) {
    return getStorageItem(`${id}-preference`) || defaultValue;
  }

  function setPreference(id, value) {
    return setStorageItem(`${id}-preference`, value);
  }

  function getRingtonePreference(id, defaultValue) {
    const agentRingtoneName = getInitialPreferenceValue(id, defaultValue);

    return getDefaultRingtoneName({
      ringtone: agentRingtoneName,
      adminRingtone: DEFAULT_RINGTONE,
      hasRestrictedRingtonePermission,
    });
  }

  const [defaultWorkspaceLayout, setDefaultWorkspaceLayout] = useState(
    getInitialPreferenceValue(PREFERENCE_IDS.DEFAULT_WORKSPACE_LAYOUT, WORKSPACE_LAYOUTS.THREE_COLUMNS)
  );

  const [incomingTaskPlacement, setIncomingTaskPlacement] = useState(
    getInitialPreferenceValue(PREFERENCE_IDS.INCOMING_TASK_PLACEMENT, DEFAULT_INCOMING_TASK_PLACEMENT)
  );

  const [ringtone, setRingtone] = useState<TRingtoneNames>(getRingtonePreference(PREFERENCE_IDS.RINGTONE, undefined));

  const [enhancedLoggingEnabled, setEnhancedLoggingEnabled] = useState(
    JSON.parse(getInitialPreferenceValue(PREFERENCE_IDS.ENHANCED_LOGGING_ENABLED, DEFAULT_ENHANCED_LOGGING_STATE))
  );

  const [incomingChatNotificationsEnabled, setIncomingChatNotificationsEnabled] = useState(
    JSON.parse(
      getInitialPreferenceValue(
        PREFERENCE_IDS.INCOMING_CHAT_NOTIFICATIONS_ENABLED,
        DEFAULT_INCOMING_CHAT_NOTIFICATIONS_STATE
      )
    )
  );

  const [logStreamFilter, setLogStreamFilter] = useState<LogStreamFilter | undefined>(undefined);

  const onChangePreference = (id, setter) => (value) => {
    setter(value);
    setPreference(id, value);
  };

  return (
    <Context.Provider
      value={{
        state: {
          logStreamFilter,
          defaultWorkspaceLayout,
          ringtone,
          incomingTaskPlacement,
          enhancedLoggingEnabled,
          incomingChatNotificationsEnabled,
        },
        actions: {
          setDefaultWorkspaceLayout: onChangePreference(
            PREFERENCE_IDS.DEFAULT_WORKSPACE_LAYOUT,
            setDefaultWorkspaceLayout
          ),
          setIncomingTaskPlacement: onChangePreference(
            PREFERENCE_IDS.INCOMING_TASK_PLACEMENT,
            setIncomingTaskPlacement
          ),
          setRingtone: onChangePreference(PREFERENCE_IDS.RINGTONE, setRingtone),
          setEnhancedLoggingEnabled: onChangePreference(
            PREFERENCE_IDS.ENHANCED_LOGGING_ENABLED,
            setEnhancedLoggingEnabled
          ),
          setLogStreamFilter,
          setIncomingChatNotificationsEnabled: onChangePreference(
            PREFERENCE_IDS.INCOMING_CHAT_NOTIFICATIONS_ENABLED,
            setIncomingChatNotificationsEnabled
          ),
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export default PreferencesProvider;
